import { z } from 'zod';
// ============================================================================
// 📊 BEHAVIOR ANALYTICS TYPES
// ============================================================================
// Enums matching Prisma schema
export const BehaviorEventType = {
    // E-commerce
    VIEW_ITEM: 'VIEW_ITEM',
    ADD_TO_CART: 'ADD_TO_CART',
    REMOVE_FROM_CART: 'REMOVE_FROM_CART',
    VIEW_CART: 'VIEW_CART',
    BEGIN_CHECKOUT: 'BEGIN_CHECKOUT',
    PURCHASE: 'PURCHASE',
    SEARCH: 'SEARCH',
    VIEW_CATEGORY: 'VIEW_CATEGORY',
    // Attention
    SCROLL_DEPTH: 'SCROLL_DEPTH',
    READ_BLOCK: 'READ_BLOCK',
    GALLERY_SWIPE: 'GALLERY_SWIPE',
    GALLERY_ZOOM: 'GALLERY_ZOOM',
    EXPAND_DESCRIPTION: 'EXPAND_DESCRIPTION',
    CLICK_CTA: 'CLICK_CTA',
    FAVORITE_ADD: 'FAVORITE_ADD',
    // Identity
    USER_LINK: 'USER_LINK',
    // Journey (Phase 1)
    PAGE_VIEW: 'PAGE_VIEW',
    PAGE_EXIT: 'PAGE_EXIT',
    // Checkout steps (Phase 2)
    CHECKOUT_STEP_VIEW: 'CHECKOUT_STEP_VIEW',
    CHECKOUT_STEP_COMPLETE: 'CHECKOUT_STEP_COMPLETE',
    CHECKOUT_STEP_ERROR: 'CHECKOUT_STEP_ERROR',
    PAYMENT_INIT: 'PAYMENT_INIT',
    // Interactions (Phase 4)
    ELEMENT_IMPRESSION: 'ELEMENT_IMPRESSION',
    SEARCH_ZERO_RESULTS: 'SEARCH_ZERO_RESULTS',
};
export const PageType = {
    HOME: 'HOME',
    CATALOG: 'CATALOG',
    BOUQUET: 'BOUQUET',
    CART: 'CART',
    CHECKOUT: 'CHECKOUT',
    CATEGORY: 'CATEGORY',
    SEARCH_PAGE: 'SEARCH_PAGE',
    FAVORITES: 'FAVORITES',
    PAYMENT_SUCCESS: 'PAYMENT_SUCCESS',
    PROFILE: 'PROFILE',
    OTHER: 'OTHER',
};
export const DeviceType = {
    DESKTOP: 'DESKTOP',
    MOBILE: 'MOBILE',
    TABLET: 'TABLET',
};
// ============================================================================
// STRICT ENUM TYPES (не произвольные строки!)
// ============================================================================
export const BlockId = {
    DESCRIPTION: 'description',
    COMPOSITION: 'composition',
    CARE: 'care',
    DELIVERY: 'delivery',
    REVIEWS: 'reviews',
};
export const CtaType = {
    ADD_TO_CART: 'add_to_cart',
    BUY_NOW: 'buy_now',
    CONTACT: 'contact',
    SHARE: 'share',
    QUICK_CHECKOUT: 'quick_checkout',
    VIEW_DETAILS: 'view_details',
};
export const CheckoutStepId = {
    AUTH: 'auth',
    CONTACT: 'contact', // @deprecated — kept for historical analytics data
    VCARD: 'vcard',
    ADDRESS: 'address', // @deprecated — kept for historical analytics data
    RECIPIENT_DELIVERY: 'recipient_delivery',
    TIMESLOT: 'timeslot',
    PAYMENT_CONFIRM: 'payment_confirm',
};
export const ViewItemSource = {
    CATALOG: 'catalog',
    SEARCH: 'search',
    CATEGORY: 'category',
    SIMILAR: 'similar',
    HOMEPAGE: 'homepage',
    FAVORITES: 'favorites',
    CART: 'cart',
};
// ============================================================================
// ZOD SCHEMAS
// ============================================================================
// UUID v4 regex
const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const uuidV4Schema = z.string().regex(uuidV4Regex, 'Invalid UUID v4');
// Enum schemas
export const behaviorEventTypeSchema = z.enum([
    'VIEW_ITEM',
    'ADD_TO_CART',
    'REMOVE_FROM_CART',
    'VIEW_CART',
    'BEGIN_CHECKOUT',
    'PURCHASE',
    'SEARCH',
    'VIEW_CATEGORY',
    'SCROLL_DEPTH',
    'READ_BLOCK',
    'GALLERY_SWIPE',
    'GALLERY_ZOOM',
    'EXPAND_DESCRIPTION',
    'CLICK_CTA',
    'FAVORITE_ADD',
    'USER_LINK',
    'PAGE_VIEW',
    'PAGE_EXIT',
    'CHECKOUT_STEP_VIEW',
    'CHECKOUT_STEP_COMPLETE',
    'CHECKOUT_STEP_ERROR',
    'PAYMENT_INIT',
    'ELEMENT_IMPRESSION',
    'SEARCH_ZERO_RESULTS',
]);
export const pageTypeSchema = z.enum([
    'HOME',
    'CATALOG',
    'BOUQUET',
    'CART',
    'CHECKOUT',
    'CATEGORY',
    'SEARCH_PAGE',
    'FAVORITES',
    'PAYMENT_SUCCESS',
    'PROFILE',
    'OTHER',
]);
export const deviceTypeSchema = z.enum(['DESKTOP', 'MOBILE', 'TABLET']);
export const blockIdSchema = z.enum([
    'description',
    'composition',
    'care',
    'delivery',
    'reviews',
]);
export const ctaTypeSchema = z.enum([
    'add_to_cart',
    'buy_now',
    'contact',
    'share',
    'quick_checkout',
    'view_details',
]);
export const viewItemSourceSchema = z.enum([
    'catalog',
    'search',
    'category',
    'similar',
    'homepage',
    'favorites',
    'cart',
]);
// Payload schemas
export const viewItemPayloadSchema = z.object({
    source: viewItemSourceSchema,
    position: z.number().int().nonnegative().optional(),
    price: z.number().nonnegative(),
    categoryId: z.string().optional(),
});
export const addToCartPayloadSchema = z.object({
    quantity: z.number().int().positive(),
    price: z.number().nonnegative(),
    categoryId: z.string().optional(),
});
export const removeFromCartPayloadSchema = z.object({
    quantity: z.number().int().positive(),
    price: z.number().nonnegative(),
});
export const viewCartPayloadSchema = z.object({
    itemCount: z.number().int().nonnegative(),
    totalValue: z.number().nonnegative(),
});
export const beginCheckoutPayloadSchema = z.object({
    itemCount: z.number().int().positive(),
    totalValue: z.number().positive(),
});
export const purchaseItemPayloadSchema = z.object({
    bouquetId: z.string().min(1),
    quantity: z.number().int().positive(),
    price: z.number().nonnegative(),
});
export const purchasePayloadSchema = z.object({
    orderId: z.string().min(1),
    orderNumber: z.string().min(1),
    totalAmount: z.number().positive(),
    itemCount: z.number().int().positive(),
    items: z.array(purchaseItemPayloadSchema).min(1),
});
export const searchPayloadSchema = z.object({
    query: z.string().min(1),
    resultsCount: z.number().int().nonnegative(),
});
export const viewCategoryPayloadSchema = z.object({
    categoryName: z.string().optional(),
});
export const scrollDepthPayloadSchema = z.object({
    depth: z.union([z.literal(25), z.literal(50), z.literal(75), z.literal(100)]),
});
export const readBlockPayloadSchema = z.object({
    blockId: blockIdSchema,
    timeMs: z.number().int().min(1500), // minimum 1500ms threshold
    viewportPercent: z.number().min(0).max(100),
});
export const gallerySwipePayloadSchema = z.object({
    imageIndex: z.number().int().nonnegative(),
    direction: z.enum(['next', 'prev']),
    totalImages: z.number().int().positive(),
});
export const galleryZoomPayloadSchema = z.object({
    imageIndex: z.number().int().nonnegative(),
});
export const expandDescriptionPayloadSchema = z.object({
    blockId: blockIdSchema,
});
export const clickCTAPayloadSchema = z.object({
    ctaType: ctaTypeSchema,
    pageName: z.string().max(50).optional(),
    position: z.string().max(30).optional(),
});
export const favoriteAddPayloadSchema = z.object({
    price: z.number().nonnegative().optional(),
    categoryId: z.string().optional(),
});
// Journey payload schemas (Phase 1)
export const pageViewPayloadSchema = z.object({
    pageName: z.string().min(1).max(50),
    referrerPage: z.string().max(50).optional(),
});
export const pageExitPayloadSchema = z.object({
    pageName: z.string().min(1).max(50),
    timeSpentMs: z.number().int().nonnegative(),
    exitType: z.enum(['navigation', 'close', 'background']),
});
// Checkout step payload schemas (Phase 2)
export const checkoutStepIdSchema = z.enum([
    'auth',
    'contact',
    'vcard',
    'address',
    'recipient_delivery',
    'timeslot',
    'payment_confirm',
]);
export const checkoutStepViewPayloadSchema = z.object({
    stepId: checkoutStepIdSchema,
    stepNumber: z.number().int().positive(),
    isAuthenticated: z.boolean(),
});
export const checkoutStepCompletePayloadSchema = z.object({
    stepId: checkoutStepIdSchema,
    stepNumber: z.number().int().positive(),
    timeSpentMs: z.number().int().nonnegative(),
    isAuthenticated: z.boolean(),
});
export const checkoutStepErrorPayloadSchema = z.object({
    stepId: checkoutStepIdSchema,
    stepNumber: z.number().int().positive(),
    errorType: z.enum(['validation', 'network', 'rate_limit']),
    errorField: z.string().optional(),
});
export const paymentInitPayloadSchema = z.object({
    totalAmount: z.number().positive(),
    itemCount: z.number().int().positive(),
    paymentMethod: z.string().min(1),
});
// Interaction payload schemas (Phase 4)
export const elementImpressionPayloadSchema = z.object({
    elementId: z.string().min(1).max(50),
    pageName: z.string().min(1).max(50),
});
export const searchZeroResultsPayloadSchema = z.object({
    query: z.string().min(1),
    suggestionsShown: z.boolean(),
});
export const userLinkPayloadSchema = z.object({});
// Payload schema map for runtime validation
export const payloadSchemaByEventType = {
    VIEW_ITEM: viewItemPayloadSchema,
    ADD_TO_CART: addToCartPayloadSchema,
    REMOVE_FROM_CART: removeFromCartPayloadSchema,
    VIEW_CART: viewCartPayloadSchema,
    BEGIN_CHECKOUT: beginCheckoutPayloadSchema,
    PURCHASE: purchasePayloadSchema,
    SEARCH: searchPayloadSchema,
    VIEW_CATEGORY: viewCategoryPayloadSchema,
    SCROLL_DEPTH: scrollDepthPayloadSchema,
    READ_BLOCK: readBlockPayloadSchema,
    GALLERY_SWIPE: gallerySwipePayloadSchema,
    GALLERY_ZOOM: galleryZoomPayloadSchema,
    EXPAND_DESCRIPTION: expandDescriptionPayloadSchema,
    CLICK_CTA: clickCTAPayloadSchema,
    FAVORITE_ADD: favoriteAddPayloadSchema,
    USER_LINK: userLinkPayloadSchema,
    PAGE_VIEW: pageViewPayloadSchema,
    PAGE_EXIT: pageExitPayloadSchema,
    CHECKOUT_STEP_VIEW: checkoutStepViewPayloadSchema,
    CHECKOUT_STEP_COMPLETE: checkoutStepCompletePayloadSchema,
    CHECKOUT_STEP_ERROR: checkoutStepErrorPayloadSchema,
    PAYMENT_INIT: paymentInitPayloadSchema,
    ELEMENT_IMPRESSION: elementImpressionPayloadSchema,
    SEARCH_ZERO_RESULTS: searchZeroResultsPayloadSchema,
};
// Full event input schema
export const behaviorEventInputSchema = z.object({
    eventId: uuidV4Schema,
    schemaVersion: z.number().int().positive().default(1),
    eventType: behaviorEventTypeSchema,
    clientTimestamp: z.string().datetime(),
    anonymousId: uuidV4Schema,
    sessionId: uuidV4Schema,
    userId: z.string().optional(),
    pageViewId: uuidV4Schema,
    pageType: pageTypeSchema,
    entityId: z.string().optional(),
    searchQueryNormalized: z.string().optional(),
    payload: z.unknown(), // validated separately by event type
    deviceType: deviceTypeSchema,
    referrerDomain: z.string().optional(),
});
// Batch request schema
export const trackEventsRequestSchema = z.object({
    events: z.array(behaviorEventInputSchema).min(1).max(50),
});
// ============================================================================
// SERVER-SIDE EVENT SENTINELS
// ============================================================================
/**
 * Sentinel UUIDs for server-originated events (webhooks, background jobs).
 * These are valid UUID v4 values that pass Zod validation but are instantly
 * recognizable in queries as non-browser events.
 */
export const SERVER_EVENT_SENTINELS = {
    /** Used as sessionId for events generated server-side (e.g. BirPay webhook) */
    SESSION_ID: '00000000-0000-4000-8000-000000000000',
    /** Used as pageViewId for events generated server-side */
    PAGE_VIEW_ID: '00000000-0000-4000-8000-000000000001',
};
// ============================================================================
// CONSTANTS
// ============================================================================
export const ANALYTICS_CONSTANTS = {
    SCHEMA_VERSION: 1,
    MAX_EVENTS_PER_REQUEST: 50,
    MAX_PAYLOAD_BYTES: 8192,
    BATCH_INTERVAL_MS: 2000,
    MAX_RETRY_QUEUE_SIZE: 100,
    READ_BLOCK_MIN_TIME_MS: 1500,
    MAX_DATE_RANGE_DAYS: 90,
    MAX_EXPORT_RECORDS: 50000,
    QUERY_TIMEOUT_MS: 30000,
};
// Rate limit constants
export const RATE_LIMIT_CONSTANTS = {
    MAX_REQUESTS_PER_MINUTE: 60,
    MAX_EVENTS_PER_MINUTE: 200,
    WINDOW_MS: 60000,
};
